import apis from '#store/api';
import { useMutation, useQuery, useQueryClient, useInfiniteQuery, InfiniteData } from '@tanstack/react-query';
import { DataApiResponse, DataCollectionApiResponse } from './types';

const CHIEF_COMPLAINTS_KEY = 'chiefComplaints';

export interface ChiefComplaint {
    id?: string;
    name?: string;
    isDefault?: boolean;
}

interface GetChiefComplaintsParams {
    skip?: number;
    take: number;
    includeDefault?: boolean;
}

interface GetChiefComplaintByIdParams {
    id: string;
}

interface CreateChiefComplaintParams {
    chiefComplaint: Omit<ChiefComplaint, 'id'>;
}

interface UpdateChiefComplaintParams {
    chiefComplaint: ChiefComplaint;
}

interface BatchDeleteChiefComplaintsParams {
    ids: string[];
}

// Fetch chief complaints list
export const useChiefComplaints = (params: GetChiefComplaintsParams) => {
    return useQuery({
        queryKey: [CHIEF_COMPLAINTS_KEY, params],
        queryFn: async () => {
            const response = await apis.chiefComplaints.getChiefComplaintsList({ params });
            return response.data as DataCollectionApiResponse<ChiefComplaint>;
        },
    });
};

// Fetch chief complaints with infinite loading
export const useInfiniteChiefComplaints = (params: Omit<GetChiefComplaintsParams, 'skip'>) => {
    return useInfiniteQuery<
        DataCollectionApiResponse<ChiefComplaint>,
        Error,
        InfiniteData<DataCollectionApiResponse<ChiefComplaint>>,
        [string, string, GetChiefComplaintsParams],
        number
    >({
        queryKey: [CHIEF_COMPLAINTS_KEY, 'infinite', params],
        initialPageParam: 0,
        queryFn: async ({ pageParam }) => {
            const response = await apis.chiefComplaints.getChiefComplaintsList({
                params: {
                    ...params,
                    skip: pageParam * params.take
                }
            });
            return response.data;
        },
        getNextPageParam: (lastPage, allPages) => {
            if (lastPage.items.length < params.take) return undefined;
            return allPages.length;
        }
    });
};

// Fetch chief complaint by id
export const useChiefComplaintById = (params: GetChiefComplaintByIdParams) => {
    return useQuery({
        queryKey: [CHIEF_COMPLAINTS_KEY, params.id],
        queryFn: async () => {
            const response = await apis.chiefComplaints.getChiefComplaintById({ params: { id: params.id } });
            return response.data as DataApiResponse<ChiefComplaint>;
        },
    });
};

// Create chief complaint
export const useCreateChiefComplaint = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: CreateChiefComplaintParams) => {
            const response = await apis.chiefComplaints.createChiefComplaint({ data: params });
            return response.data as DataApiResponse<ChiefComplaint>;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [CHIEF_COMPLAINTS_KEY] });
        },
    });
};

// Update chief complaint
export const useUpdateChiefComplaint = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: UpdateChiefComplaintParams) => {
            const response = await apis.chiefComplaints.updateChiefComplaint({ data: params });
            return response.data as DataApiResponse<ChiefComplaint>;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [CHIEF_COMPLAINTS_KEY] });
        },
    });
};

// Batch delete chief complaints
export const useBatchDeleteChiefComplaints = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: BatchDeleteChiefComplaintsParams) => {
            const response = await apis.chiefComplaints.batchDeleteChiefComplaints({ data: params });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [CHIEF_COMPLAINTS_KEY] });
        }
    });
};